import { ApiError } from "../errors.js";
import { defaultMenuCacheStore } from "../menuCache.js";
export class ClientBase {
    baseUrl;
    fetchImpl;
    onTokens;
    getTokens;
    onUnauthorized;
    menuCacheStore;
    tokens = null;
    refreshInFlight = null;
    constructor(config) {
        this.baseUrl = config.baseUrl.replace(/\/+$/, "");
        this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
        this.onTokens = config.onTokens;
        this.getTokens = config.getTokens;
        this.onUnauthorized = config.onUnauthorized;
        this.menuCacheStore =
            config.menuCacheStore === null
                ? null
                : (config.menuCacheStore ?? defaultMenuCacheStore());
    }
    setTokens(tokens) {
        this.tokens = tokens;
        this.onTokens?.(tokens);
    }
    getAccessToken() {
        return (this.getTokens?.() ?? this.tokens)?.access ?? null;
    }
    /**
     * Decodes the access-token claims for UX purposes only (role-aware UI).
     * This is NOT a security check — the backend always re-verifies the token.
     */
    claims() {
        const token = this.getAccessToken();
        if (!token)
            return null;
        const part = token.split(".")[1];
        if (!part)
            return null;
        try {
            const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
            const raw = JSON.parse(json);
            const exp = raw["exp"];
            if (typeof exp === "number" && exp <= Math.floor(Date.now() / 1000) - 10) {
                return null;
            }
            return {
                sub: String(raw["sub"] ?? ""),
                tenantId: String(raw["tid"] ?? ""),
                role: String(raw["role"] ?? ""),
            };
        }
        catch {
            return null;
        }
    }
    async ensureRefreshed() {
        if (!this.refreshInFlight) {
            this.refreshInFlight = this.refresh().finally(() => {
                this.refreshInFlight = null;
            });
        }
        return this.refreshInFlight;
    }
    async request(method, path, body, authorized = false, retried401 = false, extraHeaders) {
        const headers = { ...(extraHeaders ?? {}) };
        if (body !== undefined)
            headers["Content-Type"] = "application/json";
        if (authorized) {
            const token = this.getAccessToken();
            if (token)
                headers["Authorization"] = `Bearer ${token}`;
        }
        const init = { method, headers };
        if (body !== undefined)
            init.body = JSON.stringify(body);
        const res = await this.fetchImpl(`${this.baseUrl}${path}`, init);
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        const data = text ? JSON.parse(text) : null;
        if (!res.ok) {
            if (authorized && res.status === 401 && !retried401) {
                try {
                    await this.ensureRefreshed();
                }
                catch {
                    this.setTokens(null);
                    this.onUnauthorized?.();
                    const err = (data ?? {});
                    throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText);
                }
                return this.request(method, path, body, authorized, true, extraHeaders);
            }
            if (authorized && res.status === 401) {
                this.setTokens(null);
                this.onUnauthorized?.();
            }
            const err = (data ?? {});
            throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText);
        }
        return data;
    }
    // ---- Staff/ops clients (R8 / #1047): inventory, POs, lots, inspections, commissions, ops, vertical extensions ----
    siteBase(slug) {
        return `/v1/sites/${encodeURIComponent(slug)}`;
    }
    static query(params) {
        const q = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== "")
                q.set(k, String(v));
        }
        const s = q.toString();
        return s ? `?${s}` : "";
    }
}
//# sourceMappingURL=base.js.map